import React, { Children, CSSProperties, FC, Key, useEffect, useState } from 'react';
import styles from './index.module.scss';
import classNames from 'classnames';
import { Spin } from 'antd';
import { Input, Empty, Tree } from '@maxtropy/components';
import { DataNode } from 'antd/lib/tree';
import {
  apiV2HighEnergyDeviceDeviceTreePost,
  V2HighEnergyDeviceDeviceTreePostResponse,
} from '@maxtropy/device-customer-apis-v2';
import useLatestRequest from '../useLatestRequest';
export interface TreeProps {
  className?: string;
  style?: CSSProperties;
  changeTreeSelect?: (value: React.Key[], info: any) => void; // info就是过程或者出口入口的值
}

const StationTree: FC<TreeProps> = props => {
  const { className, changeTreeSelect, style } = props;
  const [searchParams, setSearchParams] = useState<{ name: string }>(); // 搜索
  const [expandedKeys, setExpandedKeys] = useState<string[]>([]);
  const [selectedKeys, setSelectedKeys] = useState<Key[]>([]); // 选择树
  const [originData, setOriginData] = useState<DataNode[]>([]); // 原始数据
  const [loading, setLoading] = useState<boolean>(false);
  const [data, setData] = useState<DataNode[]>();
  const { runLatest } = useLatestRequest();

  // 请求树数据
  useEffect(() => {
    setLoading(true);
    runLatest(() => apiV2HighEnergyDeviceDeviceTreePost({}), {
      onSuccess: res => {
        if (res) {
          const treeData = transformTree(res.list);
          const flattenTreeData = flattenMap(treeData);
          // setExpandedKeys(Array.from(flattenTreeData.keys()) as string[]);
          setOriginData(treeData);
          setData(treeData);
          if (!treeData.length) {
            setSelectedKeys([]);
            changeTreeSelect?.([], undefined);
          } else {
            // 默认选择第一个
            const firstDeviceKey = selectDefaltKey(treeData);

            if (firstDeviceKey) {
              const parentKeys = findParentKeys(treeData, firstDeviceKey) ?? [];
              const expandList = parentKeys.slice(0, -1) as string[];

              setExpandedKeys(expandList);
              setSelectedKeys([firstDeviceKey]);

              changeTreeSelect?.([firstDeviceKey], flattenTreeData.get(firstDeviceKey)?.info);
            } else {
              changeTreeSelect?.([], undefined);
              setSelectedKeys([]);
            }
          }
        }
      },
      onSettled: () => setLoading(false),
    });
  }, [runLatest]);

  // 前端过滤
  useEffect(() => {
    const searchName = searchParams?.name?.trim();
    if (!searchName) {
      setData(originData);
      return;
    }

    const filterTree = (nodes: DataNode[]): DataNode[] => {
      return nodes
        .map(node => {
          const titleStr = node.title?.toString() || '';
          const match = titleStr.includes(searchName);
          const children = node.children ? filterTree(node.children) : [];
          if (match || children.length > 0) {
            return { ...node, title: renderTitle(titleStr, searchName), children };
          }
          return null;
        })
        .filter(Boolean) as DataNode[];
    };

    const filtered = filterTree(originData);
    setData(filtered);
    setExpandedKeys(filtered.map(n => n.key as string)); // 搜索结果自动展开
  }, [searchParams, originData]);

  const findParentKeys = (tree: DataNode[], targetKey: Key, path: Key[] = []): Key[] | null => {
    for (const node of tree) {
      const newPath = [...path, node.key];

      if (node.key === targetKey) {
        return newPath;
      }

      if (node.children) {
        const result = findParentKeys(node.children, targetKey, newPath);
        if (result) return result;
      }
    }
    return null;
  };

  // 搜索树的选择框
  const onSelect = (selectedKeys: Key[], e: any) => {
    if (selectedKeys.length === 0) {
      return;
    }
    changeTreeSelect?.(selectedKeys, e.node.info);
    setSelectedKeys(selectedKeys);
  };

  return (
    <div className={classNames(className, styles.treeArea)} style={style}>
      <div className={styles.searchArea}>
        <Input.Search
          placeholder="请输入设备名称搜索"
          allowClear
          onSearch={v => {
            setSearchParams({ name: v });
          }}
        />
      </div>

      <div className={styles.treeBox}>
        <Spin spinning={loading}>
          {data && data.length ? (
            <Tree
              className={styles.treeStyle}
              blockNode
              treeData={data}
              expandedKeys={expandedKeys}
              selectedKeys={selectedKeys}
              onExpand={expandedKeys => {
                setExpandedKeys(expandedKeys as string[]);
              }}
              onSelect={onSelect}
              titleRender={(node: any) => (
                <div className={styles.treeTitle}>
                  <span className={styles.titleText}>{node.title}</span>
                  {node.totalRatedPower && <span className={styles.ratedPower}>额定：{node.totalRatedPower}</span>}
                </div>
              )}
            />
          ) : (
            <Empty />
          )}
        </Spin>
      </div>
    </div>
  );
};

export default StationTree;

// 使得返回的数据变成DataNode格式
export const transformTree = (tree?: V2HighEnergyDeviceDeviceTreePostResponse['list']): DataNode[] => {
  if (!tree) return [];

  return tree.map((item, idx) => {
    const children = (item.deviceLedgerInfo ?? []).map((c, cIdx) => ({
      key: `customer-${item.deviceTypeId ?? idx}-${c.id}-${c.energyUnitId ?? cIdx}`,
      title: c.assetDeviceName,
      selectable: true,
      info: {
        name: c.assetDeviceName,
        id: c.energyUnitId,
        deviceLedgerId: c.id,
      },
    }));

    const count = children.length; // 子设备数量

    return {
      key: `tenant-${item.deviceTypeId}-${idx}`,
      title: `${item.deviceTypeName}（${count}）`, // ★★额定功率！！ ★★
      selectable: false,
      info: {
        name: item.deviceTypeName,
        id: item.deviceTypeId,
      },
      totalRatedPower: `${item.totalRatedPower ?? '-'}kW`,
      children,
    };
  });
};

// 平铺所有树结构
export const flattenMap = (data: DataNode[], map: Map<Key, DataNode & { info?: any }> = new Map()) => {
  data.forEach(i => {
    map.set(i.key, i);
    if (i.children) {
      flattenMap(i.children, map);
    }
  });
  return map;
};

// 寻找第一个key
export function selectDefaltKey(treeData: (DataNode & { info?: any })[]) {
  const res: Key[] = [];
  function loop(array: (DataNode & { info?: any })[], result: Key[]) {
    for (let i = 0; i < array.length; i++) {
      if (array[i].info.deviceLedgerId) {
        result.push(array[i].key);
      } else {
        if (array[i].children && array[i].children!.length > 0) {
          loop(array[i].children!, result);
        }
      }
    }
  }
  loop(treeData, res);
  return res[0];
}

export const renderTitle = (title: string, searchValue: string) => {
  if (!searchValue) return <span>{title}</span>;
  const index = title.indexOf(searchValue);
  if (index === -1) return <span>{title}</span>;
  const before = title.slice(0, index);
  const match = title.slice(index, index + searchValue.length);
  const after = title.slice(index + searchValue.length);
  return (
    <span>
      {before}
      <span style={{ color: 'red' }}>{match}</span>
      {after}
    </span>
  );
};
